import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import AddressInput from '@/components/ui/AddressInput/AddressInput';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import { formatAddress, type ParsedAddress } from '@/helpers/addressParser';
import type { Company, CompanyAddress } from '@/types/types';
import type {
    CompanyInfoFormProps,
    CompanyInfoFormValues,
} from './ICompanyInfoForm';
import { styles } from './styles';

const addressFormatted = (addr?: CompanyAddress | null): string => {
    if (!addr) return '';
    return addr.formatted_address?.trim() || formatAddress(addr);
};

const buildInitial = (company: Company): CompanyInfoFormValues => {
    const mailing = company.mailing_address ?? null;
    const billing = company.billing_address ?? null;
    return {
        name: company.name ?? '',
        contact_email: company.contact_info?.email ?? '',
        contact_phone: company.contact_info?.phone_number ?? '',
        mailing_formatted_address: addressFormatted(mailing),
        mailing_street_number: mailing?.street_number ?? '',
        mailing_street_name: mailing?.street_name ?? '',
        mailing_subpremise: mailing?.subpremise ?? '',
        mailing_city: mailing?.city ?? '',
        mailing_state: mailing?.state ?? '',
        mailing_zip: mailing?.zip ?? '',
        mailing_country: mailing?.country ?? '',
        mailing_place_id: mailing?.place_id,
        billing_formatted_address: addressFormatted(billing),
        billing_street_number: billing?.street_number ?? '',
        billing_street_name: billing?.street_name ?? '',
        billing_subpremise: billing?.subpremise ?? '',
        billing_city: billing?.city ?? '',
        billing_state: billing?.state ?? '',
        billing_zip: billing?.zip ?? '',
        billing_country: billing?.country ?? '',
        billing_place_id: billing?.place_id,
    };
};

const CompanyInfoForm = ({ company, onSave }: CompanyInfoFormProps) => {
    const theme = useTheme();
    const [form, setForm] = useState<CompanyInfoFormValues>(() => buildInitial(company));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(buildInitial(company));
    }, [company]);

    const baseline = useMemo(() => JSON.stringify(buildInitial(company)), [company]);
    const isDirty = JSON.stringify(form) !== baseline;
    const isValid = Boolean(
        form.name.trim() &&
            form.mailing_formatted_address.trim() &&
            form.billing_formatted_address.trim(),
    );

    const applyParsedAddress = (prefix: 'mailing' | 'billing', place: ParsedAddress) => {
        setForm((prev) => ({
            ...prev,
            [`${prefix}_formatted_address`]: place.formatted_address ?? '',
            [`${prefix}_street_number`]: place.street_number ?? '',
            [`${prefix}_street_name`]: place.street_name ?? '',
            [`${prefix}_city`]: place.city ?? '',
            [`${prefix}_state`]: place.state ?? '',
            [`${prefix}_zip`]: place.zip ?? '',
            [`${prefix}_country`]: place.country ?? '',
            [`${prefix}_place_id`]:
                place.place_id ??
                (prev as unknown as Record<string, string | undefined>)[
                    `${prefix}_place_id`
                ],
        }));
    };

    const handleSubmit = async () => {
        if (!isValid || !isDirty) return;
        setSaving(true);
        try {
            await onSave(form);
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => setForm(buildInitial(company));

    return (
        <Box sx={styles.wrapper}>
            <Box sx={styles.stack}>
                <Typography sx={styles.sectionTitle}>Details</Typography>
                <Box sx={styles.fields}>
                    <FieldWrapper filled={Boolean(form.name)} required>
                        <TextField
                            fullWidth
                            label="Company Name"
                            value={form.name}
                            required
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(form.mailing_formatted_address)} required>
                        <AddressInput
                            fullWidth
                            label="Mailing Address"
                            value={form.mailing_formatted_address}
                            required
                            onChange={(value) =>
                                setForm({ ...form, mailing_formatted_address: value })
                            }
                            onSelect={(place) => applyParsedAddress('mailing', place)}
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(form.mailing_subpremise)}>
                        <TextField
                            fullWidth
                            label="Mailing Address 2"
                            value={form.mailing_subpremise}
                            onChange={(e) =>
                                setForm({ ...form, mailing_subpremise: e.target.value })
                            }
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(form.billing_formatted_address)} required>
                        <AddressInput
                            fullWidth
                            label="Billing Address"
                            value={form.billing_formatted_address}
                            required
                            onChange={(value) =>
                                setForm({ ...form, billing_formatted_address: value })
                            }
                            onSelect={(place) => applyParsedAddress('billing', place)}
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(form.billing_subpremise)}>
                        <TextField
                            fullWidth
                            label="Billing Address 2"
                            value={form.billing_subpremise}
                            onChange={(e) =>
                                setForm({ ...form, billing_subpremise: e.target.value })
                            }
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(form.contact_email)}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={form.contact_email}
                            onChange={(e) =>
                                setForm({ ...form, contact_email: e.target.value })
                            }
                        />
                    </FieldWrapper>
                    <FieldWrapper filled={Boolean(form.contact_phone)}>
                        <TextField
                            fullWidth
                            label="Phone"
                            value={form.contact_phone}
                            onChange={(e) =>
                                setForm({ ...form, contact_phone: e.target.value })
                            }
                        />
                    </FieldWrapper>
                </Box>
                <Box sx={styles.actions}>
                    <Button onClick={handleReset} disabled={!isDirty || saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={styles.saveBtn(theme)}
                        onClick={handleSubmit}
                        disabled={!isDirty || !isValid || saving}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default CompanyInfoForm;
